import { useState } from 'react';
import { useHealthAlerts } from '../hooks/useHealthAlerts';
import { usePushMeldingen } from '../hooks/usePushMeldingen';
import { timeAgo } from '../utils/timeAgo';

const SEVERITY = {
    critical: { color: '#dc2626', bg: '#fef2f2', icon: '🔴' },
    warning: { color: '#d97706', bg: '#fffbeb', icon: '🟠' },
    info: { color: '#2563eb', bg: '#eff6ff', icon: '🔵' },
};

/**
 * Notification bell + dropdown panel: open health alerts first (server, then per
 * project), below them the pushes of the last 30 days -- every buzz on the phone
 * has its text here (HavunCore docs/kb/reference/push-api.md).
 */
export function NotificationBell({ token }) {
    const { alerts, openCount, loading, dismiss } = useHealthAlerts(token);
    const { meldingen, nieuwCount, fout, markeerGezien } = usePushMeldingen(token);
    const [open, setOpen] = useState(false);
    const badgeCount = openCount + nieuwCount;

    const toggle = () => {
        if (!open) markeerGezien();
        setOpen((v) => !v);
    };

    const openAlerts = alerts.filter((a) => a.status === 'open');
    const serverAlerts = openAlerts.filter((a) => a.scope === 'server');
    const projectAlerts = openAlerts.filter((a) => a.scope === 'project');

    // Group project alerts by project name
    const byProject = projectAlerts.reduce((acc, a) => {
        const key = a.project || 'Overig';
        (acc[key] = acc[key] || []).push(a);
        return acc;
    }, {});

    const renderAlert = (a) => {
        const sev = SEVERITY[a.severity] || SEVERITY.warning;
        return (
            <div key={a.id} style={{
                display: 'flex', gap: '10px', padding: '10px 12px',
                borderBottom: '1px solid #f3f4f6', backgroundColor: sev.bg,
            }}>
                <span style={{ fontSize: '14px', lineHeight: '20px' }}>{sev.icon}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>{a.title}</div>
                    {a.body && (
                        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px', wordBreak: 'break-word' }}>{a.body}</div>
                    )}
                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>
                        {timeAgo(a.last_seen_at || a.first_seen_at)}
                    </div>
                </div>
                <button
                    onClick={() => dismiss(a.id)}
                    title="Wegklikken"
                    style={{
                        border: 'none', background: 'transparent', cursor: 'pointer',
                        color: '#9ca3af', fontSize: '16px', padding: '0 2px', alignSelf: 'flex-start',
                    }}
                >✕</button>
            </div>
        );
    };

    return (
        <>
            <button
                onClick={toggle}
                title="Meldingen"
                style={{
                    position: 'relative', fontSize: '22px', padding: '8px',
                    border: 'none', borderRadius: '8px',
                    backgroundColor: open ? '#f3f4f6' : 'transparent', cursor: 'pointer',
                }}
            >
                🔔
                {badgeCount > 0 && (
                    <span style={{
                        position: 'absolute', top: '2px', right: '2px',
                        minWidth: '18px', height: '18px', padding: '0 4px',
                        borderRadius: '9px', backgroundColor: '#dc2626', color: 'white',
                        fontSize: '11px', fontWeight: 700, lineHeight: '18px', textAlign: 'center',
                    }}>{badgeCount > 99 ? '99+' : badgeCount}</span>
                )}
            </button>

            {open && (
                <div
                    onClick={() => setOpen(false)}
                    style={{ position: 'fixed', inset: 0, zIndex: 50 }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{
                            position: 'absolute', top: '60px', right: '16px',
                            width: 'min(360px, calc(100vw - 32px))', maxHeight: '70vh', overflowY: 'auto',
                            backgroundColor: 'white', borderRadius: '12px',
                            boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                        }}
                    >
                        <div style={{
                            padding: '12px 14px', borderBottom: '1px solid #e5e7eb',
                            fontWeight: 700, fontSize: '14px', display: 'flex', justifyContent: 'space-between',
                        }}>
                            <span>Meldingen</span>
                            <span style={{ color: '#6b7280', fontWeight: 400 }}>{openCount} open</span>
                        </div>

                        {loading && (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}>Laden…</div>
                        )}

                        {!loading && openCount === 0 && (
                            <div style={{ padding: '28px 20px', textAlign: 'center', color: '#10b981', fontSize: '13px' }}>
                                ✓ Alles in orde — geen meldingen
                            </div>
                        )}

                        {serverAlerts.length > 0 && (
                            <div>
                                <div style={sectionHeaderStyle}>🖥️ Algemeen / server</div>
                                {serverAlerts.map(renderAlert)}
                            </div>
                        )}

                        {Object.entries(byProject).map(([project, list]) => (
                            <div key={project}>
                                <div style={sectionHeaderStyle}>📦 {project}</div>
                                {list.map(renderAlert)}
                            </div>
                        ))}

                        <div>
                            <div style={sectionHeaderStyle}>📲 Laatste pushberichten</div>
                            {fout && (
                                <div style={{ padding: '10px 12px', color: '#dc2626', fontSize: '12px' }}>{fout}</div>
                            )}
                            {!fout && meldingen.length === 0 && (
                                <div style={{ padding: '10px 12px', color: '#9ca3af', fontSize: '12px' }}>Geen pushberichten in de laatste 30 dagen</div>
                            )}
                            {meldingen.map((m) => (
                                <div key={`push-${m.id}`} style={{ padding: '10px 12px', borderBottom: '1px solid #f3f4f6' }}>
                                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#111827' }}>{m.titel}</div>
                                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px', wordBreak: 'break-word' }}>{m.tekst}</div>
                                    <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '2px' }}>{timeAgo(m.created_at)}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}

const sectionHeaderStyle = {
    padding: '8px 12px', backgroundColor: '#f9fafb',
    fontSize: '11px', fontWeight: 700, color: '#6b7280',
    textTransform: 'uppercase', letterSpacing: '0.03em',
};

export default NotificationBell;
